import React, { useState, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  IconLink,
  IconCheck,
  IconX,
  IconShare,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { SOCIAL_SHARE_PLATFORMS } from "../constants/socialPlatforms";
import {
  closeShareModal,
  updateShareCommentary,
  toggleFriendSelection,
  setShareModalPosting,
} from "../slices/shareModalSlice";
import { resharePost, incrementShareCount } from "../thunks/shareThunks";
import { incrementLocalShareCount } from "../../../redux/slices/postSlice";
import { getFirebaseErrorMessage } from "../../../utils/firebaseErrors";
import { useAuth } from "../../../contexts/AuthContext";
import { useConversation } from "../../../features/chat/context/ConversationProvider";
import Modal from "../../../components/ui/Modal";
import PostPreview from "./PostPreview";
import Post from "../../../pages/home/Post/Post.jsx";
import FriendSelector from "./FriendSelector";
import FriendSearchModal from "../../../components/FriendSearchModal";
import CommentaryInput from "./CommentaryInput";
import { useSendToFriend } from "../hooks/useSendToFriend";

/**
 * ShareModal
 * Premium Reshare UX with industry-standard external sharing.
 * - Floating social sidebar (desktop): platform intent URLs
 * - Native Web Share API (mobile): OS share sheet
 * - Internal reshare to feed with commentary
 * - Direct friend sharing
 */
const ShareModal = () => {
  const dispatch = useDispatch();
  const { currentUser, userData } = useAuth();
  const {
    isOpen,
    type,
    item: postData,
    commentary,
    selectedFriends,
    sentFriends,
    isPosting: isSharing,
  } = useSelector((state) => state.shareModal);
  const { openDirectConversationWithUser } = useConversation();
  const usersCache = useSelector((state) => state.usersCache?.users || {});

  // Local ephemeral UI state
  const [linkCopied, setLinkCopied] = useState(false);
  const [showFriendSearch, setShowFriendSearch] = useState(false);
  const [showFriendSection, setShowFriendSection] = useState(false);
  const copyTimeoutRef = useRef(null);

  const isReshareModal = isOpen && type !== null;
  const postId = postData?.id;

  // Validate postId format (Firestore document IDs: alphanumeric, hyphens, underscores)
  const isPostIdValid = postId && /^[a-zA-Z0-9_-]+$/.test(postId);

  // Construct share URL and title
  const shareUrl = isPostIdValid
    ? `${process.env.REACT_APP_SHARE_BASE_URL || window.location.origin}/post/${postId}`
    : '';
  const shareTitle = postData?.title || "Check out this post on WeHearthem";

  // Reset copied state and pending timeout when modal closes
  const handleClose = useCallback(() => {
    dispatch(closeShareModal());
    setLinkCopied(false);
    clearTimeout(copyTimeoutRef.current);
  }, [dispatch]);

  // --- External Sharing (Industry Standard: Platform Intent URLs) ---
  const handleExternalShare = useCallback((platform) => {
    if (!isPostIdValid) return;
    dispatch(incrementShareCount(postId));
    dispatch(incrementLocalShareCount({ postId }));

    const platformDef = SOCIAL_SHARE_PLATFORMS.find((p) => p.key === platform);
    if (platformDef) {
      const targetUrl = platformDef.buildUrl(shareUrl, shareTitle);
      window.open(
        targetUrl,
        "_blank",
        "noopener,noreferrer,width=600,height=400",
      );
    }
  }, [isPostIdValid, postId, shareUrl, shareTitle, dispatch]);

  // --- Copy Link ---
  const fallbackCopy = useCallback((text) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      setLinkCopied(true);
      toast.info("Link copied to clipboard!");
      dispatch(incrementShareCount(postId));
      dispatch(incrementLocalShareCount({ postId }));
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Copy fallback failed:", err);
      toast.error("Could not copy link");
    } finally {
      document.body.removeChild(textarea);
    }
  }, [dispatch, postId]);

  const copyToClipboard = useCallback(() => {
    if (!isPostIdValid) return;
    const text = shareUrl;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  }, [isPostIdValid, shareUrl, fallbackCopy]);

  // --- Send to Friend via hook ---
  const { handleSendToFriend, sendingToUser } = useSendToFriend({
    postId,
    postData,
    commentary,
    openDirectConversationWithUser,
    usersCache,
  });

  // Derive runtime socialLinks from platform constants + Copy Link entry.
  const platformLinks = useMemo(
    () =>
      SOCIAL_SHARE_PLATFORMS.map(({ key, name, color, Icon, iconSize }) => ({
        icon: <Icon size={iconSize} />,
        name,
        color,
        onClick: () => handleExternalShare(key),
      })),
    [handleExternalShare],
  );

  const socialLinks = useMemo(
    () => [
      ...platformLinks,
      {
        icon: linkCopied ? <IconCheck size={22} /> : <IconLink size={22} />,
        name: linkCopied ? "Copied!" : "Copy Link",
        color: linkCopied ? "bg-green-500" : "bg-gray-500",
        onClick: copyToClipboard,
      },
    ],
    [platformLinks, linkCopied, copyToClipboard],
  );

  if (!isOpen || !isReshareModal) return null;

  const handlePost = async () => {
    if (isSharing) return;
    if (!postId) {
      toast.error("Cannot share: missing post data");
      console.error("[ShareModal] handlePost: postId is undefined", { postData });
      return;
    }

    dispatch(setShareModalPosting(true));
    try {
      await dispatch(
        resharePost({
          originalPostId: postId,
          commentary,
          originalPostData: postData,
          userId: currentUser.uid,
          username: userData?.displayName || currentUser.displayName,
          userPhotoURL: currentUser.photoURL,
          userProfession: userData?.userProfession || "Member",
        }),
      ).unwrap();

      toast.success("Post shared to your feed!");
      handleClose();
    } catch (error) {
      console.error("Share failed:", error);
      toast.error(getFirebaseErrorMessage(error));
    } finally {
      dispatch(setShareModalPosting(false));
    }
  };

  // --- Web Share API (Industry Standard for Mobile) ---
  const handleNativeShare = async () => {
    if (!isPostIdValid || !navigator.share) return;
    try {
      await navigator.share({ title: shareTitle, url: shareUrl });
      dispatch(incrementShareCount(postId));
      dispatch(incrementLocalShareCount({ postId }));
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Native share failed:", err);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      hideHeader={true}
      floatingContent={
        <div className="flex flex-col gap-3">
          {socialLinks.map((link, idx) => (
            <button
              key={idx}
              onClick={link.onClick}
              className={`w-11 h-11 rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110 transition-all duration-200 active:scale-90 ${link.color}`}
              title={link.name}
              aria-label={`Share on ${link.name}`}
            >
              {link.icon}
              <span className="sr-only">{link.name}</span>
            </button>
          ))}
        </div>
      }
    >
      <div className="flex flex-col w-full relative">
        {/* User Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 ring-2 ring-gray-50">
              {currentUser?.photoURL ? (
                <img
                  src={currentUser.photoURL}
                  alt={userData?.displayName || currentUser?.displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-primary text-white font-bold text-xl uppercase">
                  {(userData?.displayName || currentUser?.displayName)?.charAt(
                    0,
                  ) || "U"}
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-gray-900 text-base leading-tight">
                {userData?.displayName || currentUser?.displayName || "User"}
              </span>
              <span className="text-gray-500 text-sm">
                {userData?.userProfession || "Member"}
              </span>
            </div>
          </div>

          {/* Interior Close Button */}
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
            aria-label="Close share modal"
          >
            <IconX size={24} />
          </button>
        </div>

        {/* Commentary Input */}
        <CommentaryInput
          commentary={commentary}
          onUpdateCommentary={(val) => dispatch(updateShareCommentary(val))}
        />

        <hr className="border-t border-gray-100 my-2 mx-6" />
        
        {/* Post Preview Card */}
        <div className="px-6 pb-6">
          <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm bg-white">
            {postData ? (
              type === 'post' ? (
                <Post
                  {...postData}
                  avatar={postData.userPhotoURL || postData.avatar}
                  profession={postData.userProfession || postData.profession}
                  postId={postData.id}
                  hideFooter={true}
                  hideSettings={true}
                  preventAutoScroll={true}
                  isInsideReshare={true}
                />
              ) : (
                <PostPreview
                  username={postData.username}
                  profession={postData.userProfession}
                  timestamp={postData.timestamp}
                  avatar={postData.userPhotoURL}
                  content={postData.content}
                  title={postData.title}
                  imageUrl={postData.imageUrl}
                  imageUrls={postData.imageUrls}
                  type={postData.type}
                  sharedPostData={postData.sharedPostData}
                  sharedPostId={postData.sharedPostId}
                />
              )
            ) : (
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
                  <div className="space-y-2 flex-1">
                    <div className="h-3 bg-gray-200 rounded w-1/3 animate-pulse" />
                    <div className="h-2 bg-gray-100 rounded w-1/4 animate-pulse" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-full animate-pulse" />
                  <div className="h-3 bg-gray-200 rounded w-5/6 animate-pulse" />
                </div>
                <div className="h-32 bg-gray-100 rounded-lg animate-pulse" />
              </div>
            )}
          </div>
        </div>{" "}

        {/* Mobile & Tablet: Inline Social Buttons (shown below xl) */}
        <div className="xl:hidden px-6 pb-4">
          <div className="flex items-center gap-3 justify-center">
            {socialLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={link.onClick}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md hover:scale-110 transition-all duration-200 active:scale-90 ${link.color}`}
                title={link.name}
                aria-label={`Share on ${link.name}`}
              >
                {link.icon}
                <span className="sr-only">{link.name}</span>
              </button>
            ))}
            {navigator.share && (
              <button
                onClick={handleNativeShare}
                className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-md hover:scale-110 transition-all duration-200 active:scale-90 bg-primary"
                title="More sharing options"
                aria-label="More sharing options"
              >
                <IconShare size={20} />
                <span className="sr-only">More sharing options</span>
              </button>
            )}
          </div>
        </div>

        {/* Friend Selection Area */}
        <div className="px-6 pb-6 border-t border-gray-50 mt-2">
          <button
            type="button"
            className="w-full flex items-center justify-between mb-0 pt-4 pb-2 text-left group"
            onClick={() => setShowFriendSection((prev) => !prev)}
          >
            <span className="text-gray-500 text-sm font-medium group-hover:text-gray-700 transition-colors">
              Share to friends
            </span>
            <span className="text-gray-400 transition-transform duration-200" style={{ transform: showFriendSection ? 'rotate(180deg)' : 'none' }}>
              <IconChevronDown size={20} />
            </span>
          </button>
          
          {showFriendSection && (
            <FriendSelector
              onToggleSelection={(id) => dispatch(toggleFriendSelection(id))}
              selectedFriends={selectedFriends}
              onMoreClick={() => setShowFriendSearch(true)}
              onSendToUser={handleSendToFriend}
              sendingToUser={sendingToUser}
              sentFriends={sentFriends}
            />
          )}
        </div>

        {/* Friend Search Modal */}
        <FriendSearchModal
          isOpen={showFriendSearch}
          selectedFriends={selectedFriends}
          onToggleSelection={(id) => dispatch(toggleFriendSelection(id))}
          onClose={() => setShowFriendSearch(false)}
        />

        {/* Footer Action */}
        <div className="flex justify-end px-6 pb-6 pt-2">
          <button
            onClick={handlePost}
            disabled={isSharing}
            className="bg-primary hover:bg-blue-600 text-white font-bold px-8 py-2 rounded-full shadow-lg shadow-blue-100 transition-all duration-200 disabled:opacity-50 active:scale-95 text-base"
          >
            {isSharing ? "Posting..." : "Post"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal;
